import React, { useState, useRef, useEffect } from "react";
import {
  Sparkles,
  Bot,
  Layers,
  Users,
  Rocket,
  Briefcase,
  Zap,
  Check,
  Phone,
  AlertCircle,
  PhoneIncoming,
  PhoneForwarded,
  Hash,
  History,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { buildWhatsappUrl } from "../data/conteudoPorNicho";

const ICON_MAP = {
  Bot,
  Sparkles,
  Layers,
  Users,
  Rocket,
  Briefcase,
  Zap,
  Check,
  Phone,
};

function CardPlano({ plano, nichoSlug }) {
  const isDestaque = plano.destaque;
  const reais = Math.floor(plano.preco);
  const centavos = (plano.preco % 1).toFixed(2).split(".")[1];

  return (
    <div
      className={`relative flex flex-col rounded-2xl p-6 transition-all ${
        isDestaque
          ? "bg-[#111111] border-2 border-green-500/40 shadow-2xl shadow-green-500/20 md:scale-105"
          : "bg-[#111111] border border-white/10 hover:border-white/20"
      }`}
      data-testid={`plano-card-${plano.id}`}
    >
      {isDestaque && (
        <div className="absolute -inset-1 bg-gradient-to-r from-green-500/30 to-emerald-500/30 rounded-2xl blur-xl opacity-50 -z-10 pointer-events-none" />
      )}

      {plano.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full shadow-lg shadow-green-500/30 whitespace-nowrap">
            <Sparkles className="w-3 h-3 text-black" />
            <span className="text-[10px] font-bold text-black uppercase tracking-wider">
              {plano.badge}
            </span>
          </span>
        </div>
      )}

      <h3 className="text-2xl font-bold text-white mb-1">{plano.nome}</h3>
      <p className="text-sm text-zinc-400 mb-4">{plano.descricao}</p>

      <div className="mb-4 pb-4 border-b border-white/5">
        <div className="flex items-baseline gap-1">
          <span className="text-zinc-500 text-base">R$</span>
          <span className="text-4xl md:text-5xl font-extrabold text-white">{reais}</span>
          <span className="text-zinc-500 text-2xl">,{centavos}</span>
          <span className="text-zinc-400 text-sm ml-1">/mês</span>
        </div>
      </div>

      <ul className="space-y-2.5 mb-6 flex-1">
        {plano.incluso.map((item, i) => {
          const Icone = ICON_MAP[item.icone] || Check;
          const indisponivel = item.disponivel === false;
          return (
            <li key={i} className="flex items-start gap-2.5">
              <div
                className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                  indisponivel
                    ? "bg-zinc-700/20 opacity-40"
                    : item.destaque
                    ? "bg-gradient-to-br from-green-400 to-emerald-600"
                    : "bg-green-500/20"
                }`}
              >
                <Icone
                  className={`w-3 h-3 ${
                    indisponivel
                      ? "text-zinc-600"
                      : item.destaque
                      ? "text-black"
                      : "text-green-400"
                  }`}
                />
              </div>
              <span
                className={`text-sm leading-relaxed ${
                  indisponivel
                    ? "text-zinc-400 line-through opacity-40"
                    : item.destaque
                    ? "text-white font-semibold"
                    : "text-zinc-300"
                }`}
              >
                {item.texto}
              </span>
            </li>
          );
        })}
      </ul>

      <a
        href={`https://app.evolzap.com/login?plano=${plano.id}&origem=site&nicho=${nichoSlug}`}
        data-testid={`plano-cta-${plano.id}`}
        className={`block w-full text-center font-bold py-3 rounded-xl transition-all ${
          isDestaque
            ? "bg-green-500 hover:bg-green-600 text-black shadow-lg shadow-green-500/30 hover:scale-[1.02]"
            : "bg-white/5 hover:bg-white/10 border border-white/10 text-white"
        }`}
      >
        {plano.cta}
      </a>

      {plano.notaRodape && (
        <p className="text-[10px] text-zinc-600 mt-4 leading-relaxed">
          {plano.notaRodape}
        </p>
      )}
    </div>
  );
}

function SecaoVoIP() {
  const recursos = [
    { icone: Phone, texto: "Discador WebRTC direto no navegador" },
    { icone: PhoneIncoming, texto: "Receber e efetuar ligações pelo ramal" },
    { icone: History, texto: "Histórico unificado com chat e mensagens" },
    {
      icone: PhoneForwarded,
      texto: "Transferência de chamadas entre ramais",
    },
    { icone: Hash, texto: "Teclado DTMF durante a chamada" },
    { icone: Layers, texto: "Disponível nos planos Start e Pro" },
  ];

  const whatsappVoip = buildWhatsappUrl(
    "Olá! Quero saber mais sobre a integração VoIP do Evolzap"
  );

  return (
    <div className="mt-16 md:mt-20 max-w-5xl mx-auto">
      <div className="flex items-center gap-4 mb-10">
        <div className="flex-1 h-px bg-white/5" />
        <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
          Recurso adicional
        </span>
        <div className="flex-1 h-px bg-white/5" />
      </div>

      <div className="relative bg-gradient-to-br from-blue-500/[0.05] via-[#111111] to-green-500/[0.05] border border-white/10 rounded-2xl p-6 md:p-10 overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-green-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div>
            <div className="inline-flex w-14 h-14 rounded-xl bg-blue-500/10 border border-blue-500/20 items-center justify-center mb-4">
              <Phone className="w-7 h-7 text-blue-400" />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 leading-tight">
              Quer atender ligações na mesma tela?
            </h3>
            <p className="text-base text-zinc-400 leading-relaxed mb-4">
              O Evolzap tem{" "}
              <span className="text-white font-semibold">
                discador VoIP integrado
              </span>{" "}
              ao painel de chat. Conecta seu PABX/SIP existente e suas ligações
              caem na mesma tela do WhatsApp, Instagram, Messenger e Telegram.
            </p>

            <div className="flex items-start gap-3 p-4 bg-yellow-500/[0.05] border border-yellow-500/20 rounded-xl">
              <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-zinc-300 leading-relaxed">
                <span className="text-yellow-400 font-semibold">
                  Importante:
                </span>{" "}
                O Evolzap é a ferramenta que integra. O{" "}
                <span className="text-white font-semibold">
                  plano de minutos/PABX
                </span>{" "}
                é contratado separadamente com sua operadora preferida.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-3">
              O que está incluso no painel
            </h4>
            {recursos.map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 bg-[#0F0F0F] border border-white/5 rounded-lg"
              >
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                  <item.icone className="w-4 h-4 text-blue-400" />
                </div>
                <span className="text-sm text-zinc-300">{item.texto}</span>
              </div>
            ))}
            <a
              href={whatsappVoip}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-voip-whatsapp"
              className="inline-flex items-center justify-center gap-2 w-full mt-2 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 text-blue-400 font-semibold py-3 rounded-xl transition-all"
            >
              <MessageCircle className="w-4 h-4" />
              Falar sobre integração VoIP
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Pricing({ conteudo }) {
  const planos = conteudo.planos || [];

  const [expandido, setExpandido] = useState(false);
  const secaoRef = useRef(null);
  const temMais = planos.length > 3;
  const planosVisiveis = expandido ? planos : planos.slice(0, 3);

  useEffect(() => {
    const el = secaoRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) setExpandido(false);
      },
      { threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="planos"
      ref={secaoRef}
      className="relative py-12 md:py-16 px-4 md:px-6 bg-[#080808] overflow-hidden"
      data-testid="pricing"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-green-500/[0.02] to-transparent pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        <div className="text-center mb-8 max-w-4xl mx-auto">
          <span className="inline-block text-sm font-semibold text-green-400 uppercase tracking-wider mb-3">
            Planos
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white mb-4 tracking-tight">
            Comece hoje, sem fidelidade
          </h2>
          <p className="text-lg md:text-xl text-zinc-400 leading-relaxed">
            Tudo incluso. Sem fidelidade. Cancele quando quiser.
          </p>
        </div>

        <div className="max-w-6xl mx-auto pt-3">
          <div className="relative hidden md:block">
            {temMais && !expandido && (
              <button
                type="button"
                onClick={() => setExpandido(true)}
                aria-label="Ver plano Ultra"
                className="flex absolute -right-6 top-1/2 -translate-y-1/2 z-20 h-16 w-16 items-center justify-center rounded-full border border-zinc-700 bg-zinc-900/90 text-white shadow-xl backdrop-blur transition hover:border-green-500 hover:text-green-400"
              >
                <ChevronRight size={36} strokeWidth={2.5} />
              </button>
            )}
            {temMais && expandido && (
              <button
                type="button"
                onClick={() => setExpandido(false)}
                aria-label="Recolher planos"
                className="flex absolute -left-6 top-1/2 -translate-y-1/2 z-20 h-16 w-16 items-center justify-center rounded-full border border-zinc-700 bg-zinc-900/90 text-white shadow-xl backdrop-blur transition hover:border-green-500 hover:text-green-400"
              >
                <ChevronLeft size={36} strokeWidth={2.5} />
              </button>
            )}
            <div className={`grid gap-6 ${expandido ? "md:grid-cols-4" : "md:grid-cols-3"}`}>
              {planosVisiveis.map((plano) => (
                <CardPlano key={plano.id} plano={plano} nichoSlug={conteudo.slug} />
              ))}
            </div>
          </div>
          <div className="grid md:hidden gap-6">
            {planos.map((plano) => (
              <CardPlano key={plano.id} plano={plano} nichoSlug={conteudo.slug} />
            ))}
          </div>
        </div>

        <SecaoVoIP />
      </div>
    </section>
  );
}
